import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import Breadcrumb from "@/components/Breadcrumb";
import { Btn, MetaTag, SectionLabel } from "@/components/ui-bits";

export default function BlogDetail() {
  const { slug } = useParams();
  const { data: p, isError } = useQuery({ queryKey: ["blog-post", slug], queryFn: async () => (await api.get(`/blog/${slug}`)).data });
  const { data: services = [] } = useQuery({ queryKey: ["services"], queryFn: async () => (await api.get("/services")).data });
  const { data: all = [] } = useQuery({ queryKey: ["blog", "all"], queryFn: async () => (await api.get("/blog")).data });

  if (isError) return <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-40 text-center"><h1 className="font-display font-black text-4xl">Artikel tidak ditemukan</h1><Link to="/blog" className="link-underline overline text-xs mt-4 inline-block">Kembali ke Blog</Link></div>;
  if (!p) return <div className="py-40 text-center overline text-xs text-muted-foreground">Memuat…</div>;

  const related = all.filter((x) => x.slug !== slug).slice(0, 3);
  const relatedServices = services.filter((s) => (p.related_services || []).includes(s.slug));
  const jsonLd = { "@context": "https://schema.org", "@type": "BlogPosting", headline: p.title, description: p.excerpt, image: p.cover_image, author: { "@type": "Organization", name: p.author || "Digitalis" }, publisher: { "@type": "Organization", name: "Digitalis" } };

  return (
    <div data-testid="blog-detail-page">
      <Seo title={p.seo_title || p.title} description={p.seo_description || p.excerpt} canonicalPath={`/blog/${p.slug}`} image={p.cover_image} jsonLd={jsonLd} />
      <article className="mx-auto max-w-[820px] px-5 md:px-10 pt-12 md:pt-16 pb-16">
        <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "Blog", to: "/blog" }, { label: p.title }]} />
        <MetaTag className="mb-4 mt-8">{p.category} · {p.author}</MetaTag>
        <h1 className="mb-8 break-words font-display text-[2.25rem] font-black leading-[0.95] tracking-tightest md:text-5xl">{p.title}</h1>
        <div className="relative overflow-hidden border border-border aspect-[16/9] mb-10"><img src={p.cover_image} alt={p.title} className="w-full h-full object-cover" /></div>
        <div className="prose-editorial" dangerouslySetInnerHTML={{ __html: p.content }} />

        {relatedServices.length > 0 && (
          <div className="mt-12 border border-border bg-secondary/40 p-5 sm:p-8">
            <SectionLabel>Layanan Terkait</SectionLabel>
            <div className="flex flex-wrap gap-3">{relatedServices.map((s) => <Link key={s.id} to={`/services/${s.slug}`} className="link-underline inline-flex min-h-11 items-center font-display text-lg font-bold">{s.name}</Link>)}</div>
          </div>
        )}
        <div className="mt-12 text-center border-t border-border pt-12"><h2 className="font-display font-black tracking-tightest text-3xl mb-6">Butuh bantuan menerapkannya?</h2><Btn to="/contact">Konsultasi dengan Digitalis</Btn></div>
      </article>

      {related.length > 0 && (
        <section className="border-t border-border py-16"><div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <SectionLabel>Artikel Lainnya</SectionLabel>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-8">{related.map((r) => <Link key={r.id} to={`/blog/${r.slug}`} className="group block"><div className="relative mb-4 aspect-[4/3] overflow-hidden rounded-2xl border border-border sm:rounded-3xl"><img src={r.cover_image} alt={r.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" loading="lazy" /></div><h3 className="font-display text-sm font-bold leading-tight group-hover:text-primary sm:text-lg">{r.title}</h3></Link>)}</div>
        </div></section>
      )}
    </div>
  );
}
